import type { AttributeValue, WriteRequest } from "@aws-sdk/client-dynamodb";
import { AwsBatchWriteQueue } from "@models/storage/AwsBatchWriteQueue";
import { loadCloudConfig, type CloudConfig } from "@models/storage/cloudConfig";
import { randomUuid } from "@models/utils/uuid";
import type { IWorkflowActivityService } from "./IWorkflowActivityService";

const K_WORKFLOW_KIND = "wact";
const K_STEP_KIND = "sact";

/** AWS workflow/step activity writer matching Unity's Phase 1 key contract. */
export class AwsWorkflowActivityModel implements IWorkflowActivityService {
  private readonly m_queue: AwsBatchWriteQueue;

  constructor(config: CloudConfig = loadCloudConfig()) {
    this.m_queue = new AwsBatchWriteQueue(config.telemetryTableName, config);
  }

  recordWorkflowStart(customerId: string, workflowId: string, userId: string): void {
    this.recordWorkflow(customerId, workflowId, userId, "start", false);
  }

  recordWorkflowStop(
    customerId: string,
    workflowId: string,
    userId: string,
    isComplete: boolean,
  ): void {
    this.recordWorkflow(customerId, workflowId, userId, "stop", isComplete);
  }

  recordStepStart(
    customerId: string,
    workflowId: string,
    stepId: string,
    userId: string,
  ): void {
    this.recordStep(customerId, workflowId, stepId, userId, "start", false);
  }

  recordStepStop(
    customerId: string,
    workflowId: string,
    stepId: string,
    userId: string,
    isComplete: boolean,
  ): void {
    this.recordStep(customerId, workflowId, stepId, userId, "stop", isComplete);
  }

  flushPending(): Promise<void> {
    return this.m_queue.flushPending();
  }

  dispose(): Promise<void> {
    return this.m_queue.dispose();
  }

  private recordWorkflow(
    customerId: string,
    workflowId: string,
    userId: string,
    eventType: "start" | "stop",
    isComplete: boolean,
  ): void {
    if (!customerId || !workflowId) return;
    const timestamp = Date.now();
    const activityId = randomUuid();
    const item = commonItem(customerId, workflowId, activityId, userId, eventType, timestamp);
    item.entity_type = { S: "workflow_activity" };
    item.sk = { S: `#workflow:${workflowId}:${K_WORKFLOW_KIND}:${timestamp}:${activityId}` };
    item.gsi1sk = { S: `${timestamp}:${workflowId}:${K_WORKFLOW_KIND}:${activityId}` };
    item.device_type = { S: "Web" };
    if (eventType === "stop") item.is_complete = { BOOL: isComplete };
    this.m_queue.enqueue(put(item));
  }

  private recordStep(
    customerId: string,
    workflowId: string,
    stepId: string,
    userId: string,
    eventType: "start" | "stop",
    isComplete: boolean,
  ): void {
    if (!customerId || !workflowId || !stepId) return;
    const timestamp = Date.now();
    const activityId = randomUuid();
    const item = commonItem(customerId, workflowId, activityId, userId, eventType, timestamp);
    item.entity_type = { S: "step_activity" };
    item.sk = { S: `#workflow:${workflowId}:${K_STEP_KIND}:${timestamp}:${activityId}` };
    item.gsi1sk = { S: `${timestamp}:${workflowId}:${K_STEP_KIND}:${activityId}` };
    item.step_id = { S: stepId };
    if (eventType === "stop") item.is_complete = { BOOL: isComplete };
    this.m_queue.enqueue(put(item));
  }
}

function commonItem(
  customerId: string,
  workflowId: string,
  activityId: string,
  userId: string,
  eventType: string,
  timestamp: number,
): Record<string, AttributeValue> {
  return {
    pk: { S: `${customerId}:workflow` },
    sk: { S: "" },
    gsi1pk: { S: `${customerId}:wf_act` },
    gsi1sk: { S: "" },
    entity_type: { S: "" },
    workflow_id: { S: workflowId },
    activity_id: { S: activityId },
    user_id: { S: userId },
    event_type: { S: eventType },
    creation_date: { N: String(timestamp) },
  };
}

function put(item: Record<string, AttributeValue>): WriteRequest {
  return { PutRequest: { Item: item } };
}
